/**
 * Part 0 — Enrichment Data Propagation: client-side context helper.
 *
 * getEnrichmentContext(api, orgId) fetches GET /api/enrichment/context/{orgId}
 * and caches the result per-org for the session so every module that defaults
 * its inputs from the organization profile shares one network round-trip.
 *
 * NOTE: `api` is the already-instantiated axios instance from useAuth() — call
 * api.get(...), never api().get(...).
 */
const _cache = new Map();
const _inflight = new Map();

export async function getEnrichmentContext(api, orgId, { force = false } = {}) {
  if (!orgId) return null;
  if (!force && _cache.has(orgId)) return _cache.get(orgId);
  if (_inflight.has(orgId)) return _inflight.get(orgId);

  const p = (async () => {
    try {
      const res = await api.get(`/enrichment/context/${orgId}`);
      _cache.set(orgId, res.data);
      return res.data;
    } catch {
      return null;
    } finally {
      _inflight.delete(orgId);
    }
  })();
  _inflight.set(orgId, p);
  return p;
}

export function clearEnrichmentContext(orgId) {
  if (orgId) _cache.delete(orgId);
  else _cache.clear();
}

/** Pretty source labels for provenance chips (mirrors backend SOURCE_LABELS). */
export const SOURCE_LABELS = {
  sec_edgar: "SEC EDGAR",
  propublica: "ProPublica",
  context_dev: "Context.dev",
  pdl: "People Data Labs",
  google_kg: "Google KG",
  wikidata: "Wikidata",
  ai_recon: "AI Recon",
  cache: "Cache",
  sandbox: "Sandbox",
  manual: "Manual",
  rules_engine: "Rules Engine",
};
